import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BellIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useNotifications } from "../../contexts/NotificationContext";
import { useChatSocket } from "../../contexts/ChatContext";

/**
 * Get human-readable ID label from notification metadata
 * @param {Object} notification - Notification object
 * @returns {string|null} Human ID label or null if not available
 */
const getHumanIdLabel = (notification) => {
  const metadata = notification?.metadata || {};
  const type = notification?.type;

  switch (type) {
    case "DISPATCH": {
      const loadNumber = metadata.loadNumber || metadata.jobNumber;
      return loadNumber ? `Load #${loadNumber}` : null;
    }
    case "INVOICE": {
      const invoiceNumber = metadata.invoiceNumber;
      return invoiceNumber ? `Invoice #${invoiceNumber}` : null;
    }
    case "SERVICE_REQUEST": {
      const requestNumber = metadata.requestNumber || metadata.workOrderNumber;
      return requestNumber ? `Request #${requestNumber}` : null;
    }
    case "MAINTENANCE": {
      const maintenanceNumber = metadata.maintenanceNumber || metadata.workOrderNumber;
      return maintenanceNumber ? `WO #${maintenanceNumber}` : null;
    }
    case "CHAT": {
      const context = metadata.context;
      if (context?.humanId && context?.entityType) {
        const displayType =
          context.entityType === "job" ? "Job" :
          context.entityType === "leg" ? "Leg" :
          context.entityType === "manifest" ? "Manifest" :
          context.entityType === "payout" ? "Payout" :
          context.entityType === "invoice" ? "Invoice" :
          context.entityType;
        return `${displayType} #${context.humanId}`;
      }
      return null;
    }
    default:
      return null;
  }
};

/**
 * Icon mapping for different notification types
 * @param {string} type - Notification type
 * @returns {JSX.Element} Icon component
 */
const getNotificationIcon = (type) => {
  const iconProps = { className: "stroke-2 h-6 w-6" };

  switch (type) {
    case "DISPATCH":
      return <TruckIcon {...iconProps} />;
    case "SERVICE_REQUEST":
      return <WrenchScrewdriverIcon {...iconProps} />;
    case "MAINTENANCE":
      return <WrenchScrewdriverIcon {...iconProps} />;
    case "INVOICE":
      return <DocumentTextIcon {...iconProps} />;
    case "EMERGENCY":
      return <ExclamationTriangleIcon {...iconProps} />;
    case "SYSTEM":
      return <ComputerDesktopIcon {...iconProps} />;
    case "CHAT":
      return <ChatBubbleLeftEllipsisIcon {...iconProps} />;
    default:
      return <BellIcon {...iconProps} />;
  }
};

/**
 * Individual notification item component with click-to-navigate functionality
 * @param {Object} props
 * @param {Object} props.item - Notification data
 * @param {Function} props.onMarkAsRead - Callback to mark notification as read
 * @param {Function} props.onDelete - Callback to delete notification
 * @param {Function} props.onNavigate - Callback triggered after navigation (e.g., to close dropdown)
 * @param {Function} props.navigate - React Router navigate function
 * @param {Function} props.openConversationById - Function to open chat conversation
 */
const NotificationItem = ({ 
  item, 
  onMarkAsRead, 
  onDelete, 
  onNavigate,
  navigate,
  openConversationById,
}) => {
  const iconClasses = classNames(
    "flex items-center justify-center p-2 rounded-full",
    {
      "bg-primary-100": !item.read,
      "bg-gray-100": item.read,
    }
  );

  const iconRingClasses = classNames("p-0.5 rounded-full ring-gray-300 mr-2", {
    "ring-2": !item.read,
  });

  const containerClasses = classNames(
    "flex flex-row items-center w-full cursor-pointer p-1.5 rounded-md transition-colors",
    {
      "bg-blue-50 hover:bg-blue-100": !item.read,
      "bg-gray-50 hover:bg-gray-100": item.read,
    }
  );

  /**
   * Handle notification click - marks as read and navigates to relevant content
   */
  const handleClick = () => {
    // Mark as read if unread
    if (!item.read) {
      onMarkAsRead(item._id);
    }

    // Get metadata for navigation
    const metadata = item.metadata || {};
    let didNavigate = false;

    // Handle navigation based on notification type
    switch (item.type) {
      case "CHAT":
        // Open chat conversation
        if (metadata.conversationId && openConversationById) {
          openConversationById(metadata.conversationId);
          didNavigate = true;
        }
        break;

      case "DISPATCH":
        // Navigate to job page
        if (metadata.jobId) {
          navigate(`/jobs/${metadata.jobId}`);
          didNavigate = true;
        } else if (metadata.legId) {
          // If only legId is available, navigate to the leg's job
          // For now, we'll try to navigate using legId as a fallback
          navigate(`/jobs?legId=${metadata.legId}`);
          didNavigate = true;
        }
        break;

      case "INVOICE":
        // Navigate to invoice page
        if (metadata.invoiceId) {
          navigate(`/invoices/${metadata.invoiceId}`);
          didNavigate = true;
        }
        break;

      case "SERVICE_REQUEST":
        // Navigate to service request
        if (metadata.requestId) {
          navigate(`/service-requests/${metadata.requestId}`);
          didNavigate = true;
        }
        break;

      case "MAINTENANCE":
        // Navigate to maintenance item
        if (metadata.maintenanceId) {
          navigate(`/maintenance/${metadata.maintenanceId}`);
          didNavigate = true;
        }
        break;

      default:
        // For other types, check if there's a generic link in metadata
        if (metadata.link) {
          navigate(metadata.link);
          didNavigate = true;
        }
        break;
    }

    // Close the dropdown after navigation
    if (didNavigate && onNavigate) {
      onNavigate();
    }
  };

  // Get human-readable ID label (e.g., "Load #12345", "Invoice #789")
  const humanIdLabel = getHumanIdLabel(item);

  return (
    <div
      className={containerClasses}
      onClick={handleClick}
      tabIndex={0}
      role="button"
    >
      <div className={iconRingClasses}>
        <div className={iconClasses}>{getNotificationIcon(item.type)}</div>
      </div>
      <div className="w-0 flex-1 leading-none flex-col min-w-0">
        <div className="flex items-center gap-1.5">
          <div
            className="text-base font-medium text-gray-900 truncate"
            title={item.title}
          >
            {item.title}
          </div>
          {humanIdLabel && (
            <span className="shrink-0 text-[10px] font-medium text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded">
              {humanIdLabel}
            </span>
          )}
        </div>
        <div
          className="text-xs text-gray-600 mt-0.5 truncate max-w-xs"
          title={item.message}
        >
          {item.message}
        </div>
        <div
          className="text-[10px] text-gray-500 mt-0.5 truncate max-w-xs"
          title={new Date(item.createdAt).toLocaleString()}
        >
          {new Date(item.createdAt).toLocaleString()}
        </div>
      </div>
      <div className="flex flex-col space-y-1 ml-2">
        {!item.read && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(item._id);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
          >
            Mark read
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item._id);
          }}
          className="text-xs text-red-600 hover:text-red-800 px-2 py-1"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const NotificationFooter = ({ onMarkAllAsRead, stats, hasUnread }) => {
  return (
    <div className="border-t bg-gray-50">
      <div className="grid grid-cols-2 divide-x">
        <button
          onClick={onMarkAllAsRead}
          disabled={!hasUnread}
          className="flex justify-center cursor-pointer ease-in-out duration-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="m-3 text-sm font-medium">Mark All Read</div>
        </button>
        <div className="flex justify-center">
          <div className="m-3 text-sm text-gray-600">
            {stats ? `${stats.unread} unread of ${stats.total}` : "Loading..."}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Notification center content component
 * Displays list of notifications with navigation support
 * @param {Object} props
 * @param {Function} props.onNavigate - Callback to close dropdown after navigation
 */
const NotificationContent = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { openConversationById } = useChatSocket();
  const {
    notifications,
    isLoading,
    stats,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    error,
  } = useNotifications();

  const hasUnread = stats?.unread > 0;

  if (error) {
    return (
      <div className="w-full p-4 text-center">
        <div className="text-red-600 text-sm">
          Failed to load notifications. Please try again.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="max-h-[400px] flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="max-h-[400px] overflow-x-auto overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BellIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                item={notification}
                onMarkAsRead={markAsRead}
                onDelete={deleteNotification}
                onNavigate={onNavigate}
                navigate={navigate}
                openConversationById={openConversationById}
              />
            ))}
          </div>
        )}
      </div>
      <NotificationFooter
        onMarkAllAsRead={markAllAsRead}
        stats={stats}
        hasUnread={hasUnread}
      />
    </div>
  );
};

export default NotificationContent;
